import type { IServerSideGetRowsRequest, ProcessCellForExportParams } from "ag-grid-enterprise";
import type { Column, GridApi, SortModelItem, ValueFormatterParams, ValueGetterParams } from "~/components/grid/AgGrid";

import { GROUP_AUTO_COLUMN_ID } from "ag-grid-enterprise";
import { formatISO } from "date-fns/formatISO";
import Papa from "papaparse";
import * as XLSX from "xlsx";

export type ExportFormat = "csv" | "xlsx";

export type ExportCellProcessor = (params: ProcessCellForExportParams) => unknown;

type ExportCell = string | number;

interface ExportCells {
	headers: string[];
	rows: ExportCell[][];
}

const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const readCellValue = (column: Column, row: Record<string, unknown>, api: GridApi): unknown => {
	const colDef = column.getColDef();
	if (typeof colDef.valueGetter === "function") {
		const params: ValueGetterParams<Record<string, unknown>> = {
			api,
			colDef,
			column,
			context: api.getGridOption("context"),
			data: row,
			getValue: (field: string) => row[field],
			node: null
		};
		return colDef.valueGetter(params);
	}
	return colDef.field ? row[colDef.field] : undefined;
};

const formatValue = (column: Column, row: Record<string, unknown>, api: GridApi, value: unknown): unknown => {
	const colDef = column.getColDef();
	const formatter = colDef.valueFormatter;
	if (typeof formatter !== "function") return value;
	const params: ValueFormatterParams<Record<string, unknown>> = {
		api,
		colDef,
		column,
		context: api.getGridOption("context"),
		data: row,
		node: null,
		value
	};
	return formatter(params) ?? "";
};

const toExportCell = (value: unknown): ExportCell => {
	if (typeof value === "number") return value;
	if (value == null) return "";
	return String(value);
};

const formatCell = (
	column: Column,
	row: Record<string, unknown>,
	api: GridApi,
	format: ExportFormat,
	processCell?: ExportCellProcessor
): ExportCell => {
	const value = readCellValue(column, row, api);
	if (!processCell) return toExportCell(formatValue(column, row, api, value));
	const params: ProcessCellForExportParams = {
		api,
		column,
		context: api.getGridOption("context"),
		formatValue: (valueToFormat: unknown) => String(formatValue(column, row, api, valueToFormat) ?? ""),
		node: null,
		parseValue: (valueToParse: string) => valueToParse,
		type: format === "csv" ? "csv" : "excel",
		value
	};
	return toExportCell(processCell(params));
};

export const buildExportFileName = (prefix: string, format: ExportFormat): string => {
	const date = formatISO(new Date(), { representation: "date" });
	const ext = format === "csv" ? "csv" : "xlsx";
	return `${prefix}-${date}.${ext}`;
};

export const readGridSortModel = (api: GridApi): SortModelItem[] =>
	api
		.getColumnState()
		.filter((c) => c.sort === "asc" || c.sort === "desc")
		.sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
		.map((c) => ({ colId: c.colId, sort: c.sort as "asc" | "desc" }));

export const buildFetchAllRequest = (api: GridApi): IServerSideGetRowsRequest => ({
	endRow: 0,
	filterModel: api.getFilterModel(),
	groupKeys: [],
	pivotCols: [],
	pivotMode: false,
	rowGroupCols: [],
	sortModel: readGridSortModel(api),
	startRow: 0,
	valueCols: []
});

const expandGroupColumn = (column: Column, displayed: Column[], api: GridApi): Column[] => {
	if (column.getColDef().field) return [column];
	return api.getRowGroupColumns().filter((groupColumn) => !displayed.includes(groupColumn));
};

export const readExportColumns = (api: GridApi, alwaysIncludeColIds: readonly string[]): Column[] => {
	const displayed = api.getAllDisplayedColumns();
	const columns = displayed.flatMap((col) => {
		const colId = col.getColId();
		if (colId.startsWith(GROUP_AUTO_COLUMN_ID)) return expandGroupColumn(col, displayed, api);
		return colId.startsWith("ag-Grid-") ? [] : [col];
	});
	const forced = alwaysIncludeColIds.flatMap((colId) => api.getColumn(colId) ?? []);
	return [...new Set([...columns, ...forced])];
};

const buildExportCells = (
	rows: readonly object[],
	api: GridApi,
	format: ExportFormat,
	processCell?: ExportCellProcessor,
	alwaysIncludeColIds: readonly string[] = []
): ExportCells => {
	const columns = readExportColumns(api, alwaysIncludeColIds);

	const headers = columns.map((col) => {
		const def = col.getColDef();
		return def.headerName ?? def.field ?? col.getColId();
	});

	const rowCells = rows.map((row) =>
		columns.map((col) => formatCell(col, row as Record<string, unknown>, api, format, processCell))
	);

	return { headers, rows: rowCells };
};

const triggerBlobDownload = (blob: Blob, fileName: string): void => {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = fileName;
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	URL.revokeObjectURL(url);
};

const downloadCsv = (cells: ExportCells, fileName: string): void => {
	const csv = Papa.unparse({ data: cells.rows, fields: cells.headers });
	const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8;" });
	triggerBlobDownload(blob, fileName);
};

const downloadXlsx = (cells: ExportCells, fileName: string, sheetName: string): void => {
	const sheet = XLSX.utils.aoa_to_sheet([cells.headers, ...cells.rows]);
	const book = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(book, sheet, sheetName);
	const bytes: ArrayBuffer = XLSX.write(book, { bookType: "xlsx", type: "array" });
	triggerBlobDownload(new Blob([bytes], { type: XLSX_MIME_TYPE }), fileName);
};

const ssrmExport = (
	rows: readonly object[],
	api: GridApi,
	format: ExportFormat,
	fileName: string,
	sheetName = "Export",
	processCell?: ExportCellProcessor,
	alwaysIncludeColIds: readonly string[] = []
): void => {
	const cells = buildExportCells(rows, api, format, processCell, alwaysIncludeColIds);
	if (format === "csv") downloadCsv(cells, fileName);
	else downloadXlsx(cells, fileName, sheetName);
};

export default ssrmExport;
